import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { hiringReferralsApiService, hiringDirectionsApiService } from '../../services/apiServices';
import {
  ReferralApplicationResponse,
  ReferralApplicationStatus,
  RecruitingDirectionDto,
} from '../../api-client';
import { getReferralApplicationStatusLabel } from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';

const HiringReferralsListPage: React.FC = () => {
  const [applications, setApplications] = useState<ReferralApplicationResponse[]>([]);
  const [directions, setDirections] = useState<RecruitingDirectionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Локальные состояния для текстовых полей
  const [userSearchInput, setUserSearchInput] = useState<string>('');
  const [candidateNameInput, setCandidateNameInput] = useState<string>('');

  // Извлекаем параметры из URL
  const userSearchFilter = searchParams.get('user_search') || '';
  const candidateNameFilter = searchParams.get('candidate_name') || '';
  const statusFilter = searchParams.get('status') || '';
  const directionIdFilter = searchParams.get('direction_id') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Синхронизация локальных инпутов с URL
  useEffect(() => {
    setUserSearchInput(userSearchFilter);
    setCandidateNameInput(candidateNameFilter);
  }, [userSearchFilter, candidateNameFilter]);

  // Загрузка направлений для фильтра
  useEffect(() => {
    const loadDirections = async () => {
      try {
        const response = await hiringDirectionsApiService.getDirections();
        setDirections(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке направлений:', err);
      }
    };
    loadDirections();
  }, []);

  // Ref для предотвращения дублирования запросов
  const requestParamsRef = useRef<string | null>(null);
  const isRequestInProgressRef = useRef<boolean>(false);

  // Загрузка заявок
  useEffect(() => {
    const paramsString = JSON.stringify({
      user_search: userSearchFilter,
      candidate_name: candidateNameFilter,
      status: statusFilter,
      direction_id: directionIdFilter,
      page: currentPage,
    });

    if (requestParamsRef.current === paramsString) {
      return;
    }

    if (isRequestInProgressRef.current) {
      return;
    }

    requestParamsRef.current = paramsString;
    isRequestInProgressRef.current = true;
    setLoading(true);
    setError(null);

    const loadApplications = async () => {
      try {
        const response = await hiringReferralsApiService.getApplications(
          limit,
          (currentPage - 1) * limit,
          statusFilter || undefined,
          undefined,
          userSearchFilter || undefined,
          candidateNameFilter || undefined,
          directionIdFilter || undefined
        );

        setApplications(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке заявок реферальной программы:', err);
        setError(err.message || 'Ошибка при загрузке заявок');
      } finally {
        isRequestInProgressRef.current = false;
        setLoading(false);
      }
    };

    loadApplications();

    return () => {
      isRequestInProgressRef.current = false;
    };
  }, [userSearchFilter, candidateNameFilter, statusFilter, currentPage, limit, directionIdFilter]);

  // Обновление параметров URL
  const updateUrlParams = useCallback(
    (newParams: Partial<Record<string, string>>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          Object.entries(newParams).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          });

          if (!('page' in newParams)) {
            params.set('page', '1');
          }

          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  // Обработчики
  const handleUserSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ user_search: userSearchInput, page: '1' });
      }
    },
    [userSearchInput, updateUrlParams]
  );

  const handleCandidateNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ candidate_name: candidateNameInput, page: '1' });
      }
    },
    [candidateNameInput, updateUrlParams]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateUrlParams({ status: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handleDirectionChange = useCallback(
    (value: string) => {
      updateUrlParams({ direction_id: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    setUserSearchInput('');
    setCandidateNameInput('');
    updateUrlParams({
      user_search: '',
      candidate_name: '',
      status: '',
      direction_id: '',
      page: '1',
    });
  }, [updateUrlParams]);

  const getStatusBadgeVariant = (status: ReferralApplicationStatus) => {
    switch (status) {
      case ReferralApplicationStatus.PENDING:
        return 'warning';
      case ReferralApplicationStatus.REVIEWING:
        return 'info';
      case ReferralApplicationStatus.ACCEPTED:
        return 'success';
      case ReferralApplicationStatus.REJECTED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Заявки реферальной программы</h2>
          </div>

          {/* Фильтры */}
          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по пользователю (email, имя пользователя или ФИО)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email, имя пользователя или ФИО"
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                onKeyDown={handleUserSearchKeyDown}
              />
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по имени кандидата</Form.Label>
              <Form.Control
                type="text"
                placeholder="Имя кандидата"
                value={candidateNameInput}
                onChange={(e) => setCandidateNameInput(e.target.value)}
                onKeyDown={handleCandidateNameKeyDown}
              />
            </div>
            <div className="col-md-2 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="pending">Ожидает обработки</option>
                <option value="reviewing">На рассмотрении</option>
                <option value="accepted">Принято</option>
                <option value="rejected">Отклонено</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2">
              <Form.Label>Направление</Form.Label>
              <Form.Select
                value={directionIdFilter}
                onChange={(e) => handleDirectionChange(e.target.value)}
              >
                <option value="">Все</option>
                {directions.map((direction) => (
                  <option key={direction.id} value={direction.id}>
                    {direction.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка заявок...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Пользователь</th>
                    <th style={{ maxWidth: '200px' }}>Кандидат</th>
                    <th>Направление</th>
                    <th>Тип резюме</th>
                    <th>Статус</th>
                    <th>Дата создания</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications && applications.length > 0 ? (
                    applications.map((app) => {
                      const userEmail = app.user?.email || '—';
                      const userName = app.user?.username || '—';
                      const userFullName = app.user?.profile?.full_name;
                      const directionName = app.direction?.name || '—';
                      return (
                        <tr key={app.id}>
                          <td>{app.id}</td>
                          <td>
                            {userFullName && (
                              <div>
                                <strong>{userFullName}</strong>
                              </div>
                            )}
                            <div>
                              <strong>{userName}</strong>
                            </div>
                            <small className="text-muted">{userEmail}</small>
                          </td>
                          <td style={{ wordBreak: 'break-word', maxWidth: '200px' }}>
                            {app.candidate_name && app.candidate_name.length > 150
                              ? `${app.candidate_name.substring(0, 150)}...`
                              : app.candidate_name}
                          </td>
                          <td>{directionName}</td>
                          <td>
                            <Badge bg="secondary">
                              {app.resume_type === 'file' ? 'Файл' : 'URL'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(app.status)}>
                              {getReferralApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td>
                            <Link to={`/hiring/referrals/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center">
                        Заявки не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Пагинация */}
              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={applications.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default HiringReferralsListPage;
